import React, { useEffect, useState } from 'react'
import { BubbleMessagesProps } from './types'
import { useScrollToBottom } from 'hooks/use-scroll-to-bottom'
import { BubbleMessageThinkTime } from 'features/interview/contants'
import { VacancyDefaultUIBubbleType } from 'api/endpoints/openapi-generated.schemas'
import { MessageComponent } from 'components/messages_new/message-component'
import { MessageButton } from 'components/messages_new/message-button'
import { MessageFormEmail } from 'components/messages_new/message-form-email'
import { MessageFormPhone } from 'components/messages_new/message-form-phone'
import { NavigateOptions, useRouter } from '@tanstack/react-router'
import { TypingIndicator } from 'components/message/typing-indicator'
import {
  useEnsureInterviewApiV1InterviewsEnsurePost,
  useSubmitFormsApiV1InterviewsInterviewIdSubmitFormsPost
} from 'api/endpoints/openapi-generated'
import { substituteVariables } from 'utils/string'
import { useAnalytics } from 'hooks/use-analytics'
import { MessageFormEmailPhoneContact } from 'components/messages_new/message-form-email-phone-contact'
import { MessageCustomForm } from 'components/messages_new/message-custom-form'
import { captureError } from 'utils/sentry'
import { InterviewStageEnum } from 'consts/company-alias.consts'
import { MessageFormEmailPhone } from 'components/messages_new/message-form-email-phone'
import { UserInterviewInitData } from 'features/interview/pages/types'
import { AxiosError } from 'axios'
import { getVariables } from '../../features/interview/pages/variables.utils'
import { useTranslation } from 'react-i18next'
import { createEvent, restore, sample } from 'effector'
import { useUnit } from 'effector-react/effector-react.mjs'
import { resetMessageForm } from '../messages_new/message-form.events'
import { MessageTypeEnum } from '../../constants/call'

export const interviewWelcomeStepPassed = createEvent<void>()

sample({
  clock: interviewWelcomeStepPassed,
  target: resetMessageForm
})

const setIsThinking = createEvent<boolean>()
const $isThinking = restore(setIsThinking, false).reset([
  interviewWelcomeStepPassed
])

const setBubbleMessageIndex = createEvent<number>()
const $bubbleMessageIndex = restore(setBubbleMessageIndex, 0).reset([
  interviewWelcomeStepPassed
])

export const BubbleMessages: React.FC<BubbleMessagesProps> = ({
  bubbleMessages,
  interviewerAvatar,
  vacancy,
  variables,
  setVariables,
  phoneCountryCode,
  interviewId,
  onBubbleMessageIndexChange,
  setInterviewId
}) => {
  const { t } = useTranslation()
  const { scrollToRef } = useScrollToBottom()
  const isThinking = useUnit($isThinking)
  const bubbleMessageIndex = useUnit($bubbleMessageIndex)
  const [isVacancyPreparing, setIsVacancyPreparing] = useState(false)
  const router = useRouter()
  const { capture } = useAnalytics()

  useEffect(() => {
    onBubbleMessageIndexChange?.(bubbleMessageIndex)
  }, [bubbleMessageIndex, onBubbleMessageIndexChange])

  const handleButtonMessageClick = (text: string) => {
    capture('button_bubble_message_clicked', {
      vacancy_id: vacancy.id,
      bubble_message_text: text
    })
    setIsThinking(true)
    setTimeout(() => {
      setBubbleMessageIndex(bubbleMessageIndex + 1)
      setIsThinking(false)
    }, BubbleMessageThinkTime)
  }

  useEffect(() => {
    if (bubbleMessageIndex < bubbleMessages.length - 1) {
      const hasMoreMessages = bubbleMessageIndex < bubbleMessages.length - 1
      const currentMessage = bubbleMessages[bubbleMessageIndex]
      const isNextFromAivy =
        hasMoreMessages &&
        bubbleMessages[bubbleMessageIndex + 1].message_type ===
          MessageTypeEnum.incoming

      const { type } = currentMessage
      const isCurrentInteractive =
        type === VacancyDefaultUIBubbleType.button ||
        type === VacancyDefaultUIBubbleType.form_email ||
        type === VacancyDefaultUIBubbleType.form_phone ||
        type === VacancyDefaultUIBubbleType.form_email_phone ||
        type === VacancyDefaultUIBubbleType.form_email_phone_contact ||
        type === VacancyDefaultUIBubbleType.link ||
        type === VacancyDefaultUIBubbleType.custom_form

      if (!isCurrentInteractive) {
        if (isNextFromAivy) {
          setIsThinking(true)
          const timeout = setTimeout(() => {
            setBubbleMessageIndex(bubbleMessageIndex + 1)
            setIsThinking(false)
          }, BubbleMessageThinkTime)

          return () => clearTimeout(timeout)
        } else {
          setBubbleMessageIndex(bubbleMessageIndex + 1)
        }
        // eslint-disable-next-line no-empty
      } else {
      }
    }
  }, [bubbleMessageIndex, bubbleMessages])

  const { mutate: ensureInterviewStarted } =
    useEnsureInterviewApiV1InterviewsEnsurePost()
  const { mutate: uploadForms } =
    useSubmitFormsApiV1InterviewsInterviewIdSubmitFormsPost()

  const handleSubmitCustomForm = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    isFailed: boolean,
    errorMessage: string
  ) =>
    new Promise<void>((resolve) => {
      if (!interviewId) {
        captureError('interviewId is undefined', {
          vacancyId: vacancy.id
        })
        return
      }
      uploadForms(
        {
          interviewId,
          data: {
            forms: [
              {
                data,
                error_message: errorMessage,
                is_failed: isFailed
              }
            ]
          }
        },
        {
          onSuccess() {
            capture('user_custom_form_filled', {
              vacancy_id: vacancy.id,
              interview_id: interviewId,
              is_failed: isFailed
            })
            if (!isFailed) {
              setIsThinking(true)
              setTimeout(() => {
                setBubbleMessageIndex(bubbleMessageIndex + 1)
                setIsThinking(false)
              }, BubbleMessageThinkTime)
              resolve()
            } else {
              setIsThinking(false)
              router.navigate({
                search: {
                  stage: InterviewStageEnum.Results
                }
              } as NavigateOptions)
            }
          }
        }
      )
    })

  const handleSubmitUserInitData = (data: UserInterviewInitData) =>
    new Promise<void>((resolve, reject) => {
      ensureInterviewStarted(
        {
          data: {
            vacancy_id: vacancy.id,
            user_name: data.userName,
            user_second_name: data.userSecondName,
            user_contact_email: data.userContactEmail,
            user_contact_phone: data.userContactPhone,
            user_additional_contact: data.userExternalContact
          }
        },
        {
          onSuccess(response) {
            capture('user_main_info_provided', {
              vacancy_id: vacancy.id,
              interview_id: response.data.id,
              user_first_name: data.userName,
              user_email_host: data.userContactEmail?.split('@')[1]
            })
            setVariables?.(getVariables(vacancy, response.data))
            setInterviewId?.(response.data.id)

            setIsThinking(true)
            setTimeout(() => {
              setBubbleMessageIndex(bubbleMessageIndex + 1)
              setIsThinking(false)
            }, BubbleMessageThinkTime)
            resolve()
          },
          onError(error) {
            // Check if error status is 422
            if ((error as AxiosError)?.status === 422) {
              setIsVacancyPreparing(true)
            }
            reject(error)
          }
        }
      )
    })

  const isGroupedTypes = (
    type_a: VacancyDefaultUIBubbleType,
    type_b: VacancyDefaultUIBubbleType
  ) => {
    if (type_a === type_b) return true

    const sameTypes: Array<VacancyDefaultUIBubbleType> = [
      VacancyDefaultUIBubbleType.message,
      VacancyDefaultUIBubbleType.html_message,
      VacancyDefaultUIBubbleType.custom_form
    ]
    if (sameTypes.includes(type_a) && sameTypes.includes(type_b)) {
      return true
    }

    return false
  }

  if (isVacancyPreparing) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-lg text-gray-800">
            {t('welcome.vacancy_preparing')}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {bubbleMessages
        .slice(0, bubbleMessageIndex + 1)
        .map(
          (
            { type, link, is_link_internal, message_type, text, form_schema },
            index
          ) => {
            const escapedText = substituteVariables(text || '', variables)
            const isLast = index === bubbleMessageIndex
            const isGrouped =
              index > 0 && isGroupedTypes(type, bubbleMessages[index - 1].type)

            const isLastInGroup =
              (index === bubbleMessageIndex && !isThinking) ||
              (index < bubbleMessages.length - 1 &&
                type !== bubbleMessages[index + 1].type)

            if (type === VacancyDefaultUIBubbleType.custom_form) {
              if (!form_schema) {
                captureError('form_schema is undefined', {
                  vacancy_id: vacancy.id,
                  interview_id: interviewId
                })

                return <div key={index}>Form schema is undefined</div>
              }

              return (
                <MessageCustomForm
                  key={index}
                  formSchema={form_schema}
                  t={t}
                  type={message_type as MessageTypeEnum}
                  text={escapedText}
                  onSubmit={handleSubmitCustomForm}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.message) {
              return (
                <MessageComponent
                  key={index}
                  text={escapedText}
                  type={message_type as MessageTypeEnum}
                  isLastInGroup={isLastInGroup}
                  isGrouped={isGrouped}
                  senderAvatar={
                    message_type === MessageTypeEnum.incoming
                      ? interviewerAvatar
                      : undefined
                  }
                  senderName={vacancy.interviewer?.full_name}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.button) {
              return (
                <MessageButton
                  key={index}
                  type={message_type as MessageTypeEnum}
                  text={escapedText}
                  onClick={() => handleButtonMessageClick(escapedText)}
                  isClicked={!isLast}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.form_email) {
              return (
                <MessageFormEmail
                  t={t}
                  type={MessageTypeEnum.outgoing}
                  text={escapedText}
                  key={index}
                  onSubmit={async (data) => {
                    await handleSubmitUserInitData({
                      userName: data.name,
                      userSecondName: data.surname,
                      userContactEmail: data.email
                    })
                  }}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.form_phone) {
              return (
                <MessageFormPhone
                  t={t}
                  phoneCountryCode={phoneCountryCode}
                  type={MessageTypeEnum.outgoing}
                  text={escapedText}
                  key={index}
                  onSubmit={async (data) => {
                    await handleSubmitUserInitData({
                      userName: data.name,
                      userSecondName: data.surname,
                      userContactPhone: data.phone
                    })
                  }}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.form_email_phone) {
              return (
                <MessageFormEmailPhone
                  t={t}
                  type={MessageTypeEnum.outgoing}
                  phoneCountryCode={phoneCountryCode}
                  text={escapedText}
                  key={index}
                  onSubmit={async (data) => {
                    await handleSubmitUserInitData({
                      userName: data.name,
                      userSecondName: data.surname,
                      userContactPhone: data.phone,
                      userContactEmail: data.email
                    })
                  }}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.form_email_phone_contact) {
              return (
                <MessageFormEmailPhoneContact
                  t={t}
                  type={MessageTypeEnum.outgoing}
                  text={escapedText}
                  key={index}
                  onSubmit={async (data) => {
                    await handleSubmitUserInitData({
                      userName: data.name,
                      userSecondName: data.surname,
                      userContactPhone: data.phone,
                      userContactEmail: data.email,
                      userExternalContact: data.externalContact
                    })
                  }}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.link) {
              return (
                <MessageButton
                  key={index}
                  type={message_type as MessageTypeEnum}
                  text={escapedText}
                  onClick={() => {
                    if (!link) return

                    if (is_link_internal) {
                      void router.navigate({ to: link })
                      return
                    }

                    const prefix = 'https://'
                    if (!/^https?:\/\//i.test(link)) {
                      window.open(prefix + link, '_blank')
                    } else {
                      window.open(link, '_blank')
                    }
                  }}
                  isClicked={!isLast}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            if (type === VacancyDefaultUIBubbleType.html_message) {
              return (
                <MessageComponent
                  displayTextAsRawHTML
                  key={index}
                  text={escapedText}
                  type={message_type as MessageTypeEnum}
                  isLastInGroup={isLastInGroup}
                  isGrouped={isGrouped}
                  senderAvatar={
                    message_type === MessageTypeEnum.incoming
                      ? interviewerAvatar
                      : undefined
                  }
                  senderName={vacancy.interviewer?.full_name}
                  ref={isLast ? scrollToRef : null}
                />
              )
            }

            return <div key={index}>Not supported message component</div>
          }
        )}
      {isThinking && (
        <TypingIndicator
          ref={scrollToRef}
          className={
            (bubbleMessages[bubbleMessageIndex].type as MessageTypeEnum) ===
            MessageTypeEnum.outgoing
              ? 'mt-[40px]'
              : ''
          }
        />
      )}
    </div>
  )
}
